/**
 * 主布局组件 - 重构版
 * Feature: mobile-responsive-framework
 * 包含侧边栏导航、顶部栏、内容区域和移动端底部导航
 * 应用工业蓝 + 暖橙配色方案
 */
import React, { useState, useEffect } from 'react';
import { Layout, ConfigProvider } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { useResponsiveConfig } from '../../responsive/hooks/useResponsiveConfig';
import { useAuthStore } from '../../store/authStore';
import MobileBottomNav from './MobileBottomNav';
import { Sidebar } from './Sidebar';
import { HeaderBar } from './HeaderBar';
import { AppRoutes } from './routeConfig';
import zhCN from 'antd/locale/zh_CN';

const { Content } = Layout;

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isTablet, containerPadding } = useResponsiveConfig();
  const { user, logout } = useAuthStore();
  
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);

  // 根据屏幕大小自动折叠侧边栏
  useEffect(() => {
    if (isTablet) {
      setCollapsed(true);
    }
  }, [isTablet]);

  // 处理菜单点击
  const handleMenuClick: MenuProps['onClick'] = (e) => {
    navigate(e.key);
    if (isMobile) {
      setMobileDrawerVisible(false);
    }
  };

  // 处理登出
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // 获取当前选中的菜单项
  const getSelectedKeys = () => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') return ['/dashboard'];
    
    // 进度跟踪模块 - 匹配完整路径
    if (path.startsWith('/progress/')) {
      // 处理详情页面，返回列表页路径
      if (path.match(/^\/progress\/additions\/[^/]+$/)) return ['/progress/additions'];
      if (path.match(/^\/progress\/rectifications\/[^/]+$/)) return ['/progress/rectifications'];
      return [path];
    }
    
    // 匹配一级路径
    const firstLevelPath = '/' + path.split('/')[1];
    return [firstLevelPath];
  };

  // 获取展开的菜单项
  const getOpenKeys = () => {
    const path = location.pathname;
    if (path.startsWith('/data-management') || 
        path.startsWith('/price-config') || 
        path.startsWith('/user-management') ||
        path.startsWith('/user-permission-manage') ||
        path.startsWith('/permission-matrix')) {
      return ['admin'];
    }
    if (path.startsWith('/progress')) {
      return ['progress'];
    }
    if (path.startsWith('/reports-new')) {
      return ['reports-new'];
    }
    return [];
  };

  return (
    <ConfigProvider locale={zhCN}>
      <Layout
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #d8e0e5 0%, #c6d0d7 38%, #b3c0ca 100%)',
        }}
      >
        {/* 侧边栏 */}
        <Sidebar
          collapsed={collapsed}
          isMobile={isMobile}
          mobileDrawerVisible={mobileDrawerVisible}
          selectedKeys={getSelectedKeys()}
          openKeys={getOpenKeys()}
          userPermissions={user?.permissions}
          onMenuClick={handleMenuClick}
          onDrawerClose={() => setMobileDrawerVisible(false)}
        />

        {/* 主内容区域 */}
        <Layout
          style={{
            marginLeft: isMobile ? 0 : collapsed ? 80 : 220,
            transition: 'margin-left 0.2s',
            background: 'transparent',
          }}
        >
          {/* 顶部栏 */}
          <HeaderBar
            collapsed={collapsed}
            isMobile={isMobile}
            containerPadding={containerPadding}
            userName={user?.realName || user?.username}
            onToggleCollapse={() => setCollapsed(!collapsed)}
            onLogout={handleLogout}
          />

          {/* 内容区域 */}
          <Content
            style={{
              padding: containerPadding,
              minHeight: 'calc(100vh - 64px)',
              paddingBottom: isMobile ? 80 : containerPadding,
              background: 'transparent',
              overflow: 'visible',
            }}
          >
            <AppRoutes />
          </Content>
        </Layout>

        {/* 移动端底部导航 */}
        {isMobile && (
          <MobileBottomNav onMenuClick={() => setMobileDrawerVisible(true)} />
        )}
      </Layout>
    </ConfigProvider>
  );
};

export default MainLayout;
